// Global packages and components
import { Link } from '@remix-run/react'
import { Heading, Price, ProductImage } from '~/components'
import { getProductPageLink } from '~/utils'

// Types
import type { ProductSearchItem } from '~/types'

interface Props {
  products: ProductSearchItem[]
  title?: string
}

/*
  Sibling products from the same category. Rendered from loader data with plain
  links so the product layer is reachable without running JavaScript. Schema is
  handled by the route's JSON-LD, so no microdata here.
*/
const RelatedProducts = ({ products, title = 'Related products' }: Props) => {
  if (!products || products.length === 0) return null

  return (
    <section className="container mb-16">
      <Heading
        tag="h2"
        size="h4"
        className="text-center"
      >
        {title}
      </Heading>
      <ul className="grid grid-cols-2 gap-3 md:grid-cols-4 lg:gap-5">
        {products.map((product, i) => {
          const link = getProductPageLink({
            productType: product.productType,
            stockCode: product.url?.stockCode,
            slug: product.url?.slug,
            variant: product.url?.option?.value,
          })

          return (
            <li key={i}>
              <div className="flex h-full flex-col rounded-2xl bg-neutral-100 p-3 md:p-4">
                <Link
                  to={link}
                  className="mb-4 block"
                  tabIndex={-1}
                  aria-hidden="true"
                >
                  <ProductImage
                    src={
                      product.image?.imageUrl ?? 'https://placehold.co/600x400'
                    }
                    alt={product.name || 'Product image'}
                    loading="lazy"
                  />
                </Link>
                <h3 className="mb-2 text-base font-semibold md:text-lg">
                  <Link to={link}>{product.name}</Link>
                </h3>
                <div className="mt-auto">
                  {product.productType == 'bulk' && (
                    <p className="mb-1 text-xs">From:</p>
                  )}
                  <Price
                    highlight
                    price={product.guidePrice}
                    salePrice={product.salePrice}
                  />
                </div>
              </div>
            </li>
          )
        })}
      </ul>
    </section>
  )
}

export default RelatedProducts
